import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";

// My components
import Card from "../../components/admin/Card";
import HeaderCard from "../../components/common/HeaderCard";
import BackButton from "../../components/common/BackButton";
import { CardGrid } from "../../components/common/CardGrid";
import Loader from "../../components/common/Loader";

// My css
import css from "../../css/admin/verticals-page.module.css";

import { SERVER_ORIGIN, validation } from "../../utilities/constants";
import { refreshScreen, hasReadOnlyAccess, getDashboardPath } from "../../utilities/helper_functions";

//////////////////////////////////////////////////////////////////////////////////////////////

const VerticalsPage = () => {
  const [allVerticals, setAllVerticals] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [newVertical, setNewVertical] = useState({
    name: "",
    desc: "",
    imgSrc: "",
  });

  const navigate = useNavigate();
  const isReadOnly = hasReadOnlyAccess();
  const currentUserType = sessionStorage.getItem('userType');

  useEffect(() => {
    async function canVisitPage() {
      setIsLoading(true);
      try {
        let verifyEndpoint = '';
        let headers = {
          "Content-Type": "application/json",
          "auth-token": sessionStorage.getItem("token"),
        };

        // Choose the correct verification endpoint based on user type
        if (currentUserType === 'admin') {
          verifyEndpoint = `${SERVER_ORIGIN}/api/admin/auth/verify-token`;
          const adminId = process.env.REACT_APP_ADMIN_ID;
          const adminPassword = process.env.REACT_APP_ADMIN_PASSWORD;
          const basicAuth = btoa(`${adminId}:${adminPassword}`);
          headers.Authorization = `Basic ${basicAuth}`;
        } else if (currentUserType === 'regionem') {
          verifyEndpoint = `${SERVER_ORIGIN}/api/regionem/auth/verify-token`;
        } else {
          navigate("/login");
          return;
        }

        const response = await fetch(verifyEndpoint, {
          method: "POST",
          headers: headers,
        });

        const result = await response.json();

        if (response.status >= 400 && response.status < 600) {
          if (response.status === 401) {
            navigate(`/login?userType=${currentUserType}`);
          }
        } else if (response.ok && response.status === 200) {
          // Token verified, continue to load page
        } else {
          navigate(`/login?userType=${currentUserType}`);
        }
      } catch (err) {
        console.log(err.message);
        navigate("/login");
      }
      setIsLoading(false);
    }

    async function getAllVerticals() {
      setIsLoading(true);

      try {
        // For Region EM users, we'll use the student endpoint which doesn't require admin auth
        // since they only need read-only access to view content
        let endpoint = '';
        let headers = {
          "Content-Type": "application/json",
          "auth-token": sessionStorage.getItem("token"),
        };

        if (currentUserType === 'admin') {
          endpoint = `${SERVER_ORIGIN}/api/admin/auth/verticals/all`;
          const adminId = process.env.REACT_APP_ADMIN_ID;
          const adminPassword = process.env.REACT_APP_ADMIN_PASSWORD;
          const basicAuth = btoa(`${adminId}:${adminPassword}`);
          headers.Authorization = `Basic ${basicAuth}`;
        } else if (currentUserType === 'regionem') {
          // Use student endpoint for read-only access
          endpoint = `${SERVER_ORIGIN}/api/student/auth/verticals/all`;
        }

        const response = await fetch(endpoint, {
          method: "GET",
          headers: headers,
        });

        const result = await response.json();

        setIsLoading(false);

        if (response.status >= 400 && response.status < 600) {
          if (response.status === 401) {
            navigate(`/login?userType=${currentUserType}`);
          } else if (response.status === 500) {
            toast.error(result.statusText);
          }
        } else if (response.ok && response.status === 200) {
          setAllVerticals(result.allVerticals);
        } else {
          // for future
        }
      } catch (err) {
        console.log(err.message);
        setIsLoading(false);
      }
    }

    canVisitPage();
    getAllVerticals();
  }, [navigate, currentUserType]);

  //////////////////////////////// Add Vertical Modal ////////////////////////////////////////

  const ref = useRef(null);
  const refClose = useRef(null);

  async function openAddModal() {
    ref.current.click();
  }

  function onAddChange(e) {
    const updatedVertical = {
      ...newVertical,
      [e.target.name]: e.target.value,
    };
    setNewVertical(updatedVertical);

    // (updatedVertical);
  }

  async function handleAddVertical() {
    // todo: validate input
    try {
      const adminId = process.env.REACT_APP_ADMIN_ID;
      const adminPassword = process.env.REACT_APP_ADMIN_PASSWORD;
      const basicAuth = btoa(`${adminId}:${adminPassword}`);

      const response = await fetch(
        `${SERVER_ORIGIN}/api/admin/auth/verticals/add`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "auth-token": sessionStorage.getItem("token"),
            Authorization: `Basic ${basicAuth}`,
          },
          body: JSON.stringify(newVertical),
        }
      );

      const result = await response.json();

      if (response.status >= 400 && response.status < 600) {
        if (response.status === 401) {
          navigate("/admin/login"); // login or role issue
        } else if (response.status === 500) {
          toast.error(result.statusText);
        }
      } else if (response.ok && response.status === 200) {
        refreshScreen();
        // set fields in modal to empty if not refreshing scrn
      } else {
        // for future
      }

      refClose.current.click();
    } catch (err) {
    }
  }

  //////////////////////////////////////////////////////////////////////////////////////////

  async function handleAddOrViewCourses(e) {
    const verticalId = e.target.id;
    // (verticalId);
    navigate(`/admin/verticals/${verticalId}/courses/all`);
  }

  // Toggle hide/unhide vertical across the platform
  async function handleToggleVisibility(verticalId, isCurrentlyHidden) {
    try {
      const adminId = process.env.REACT_APP_ADMIN_ID;
      const adminPassword = process.env.REACT_APP_ADMIN_PASSWORD;
      const basicAuth = btoa(`${adminId}:${adminPassword}`);

      const response = await fetch(
        `${SERVER_ORIGIN}/api/admin/auth/verticals/${verticalId}/toggle-visibility`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "auth-token": sessionStorage.getItem("token"),
            Authorization: `Basic ${basicAuth}`,
          },
        }
      );

      const result = await response.json();

      if (!response.ok) {
        toast.error(result.statusText || "Failed to update visibility");
        return;
      }

      // Update local state so the toggle feels instant
      setAllVerticals((prev) =>
        prev.map((v) =>
          v._id === verticalId ? { ...v, isHidden: !isCurrentlyHidden } : v
        )
      );
    } catch (error) {
      console.error("Error toggling vertical visibility:", error);
      toast.error("Failed to update visibility");
    }
  }

  ////////////////////////////////////// Delete Vertical Modal ///////////////////////////////////////////////////
  const ref2 = useRef(null);
  const refClose2 = useRef(null);
  const [toDeleteVerticalId, setToDeleteVerticalId] = useState("");
  const [confirmText, setConfirmText] = useState("");

  function openDeleteModal(e) {
    ref2.current.click();
    setToDeleteVerticalId(e.target.id);
  }

  function onConfirmTextChange(e) {
    setConfirmText(e.target.value);
  }

  async function handleDeleteVertical() {
    const verticalId = toDeleteVerticalId;
    // todo: validate input
    try {
      const adminId = process.env.REACT_APP_ADMIN_ID;
      const adminPassword = process.env.REACT_APP_ADMIN_PASSWORD;
      const basicAuth = btoa(`${adminId}:${adminPassword}`);

      const response = await fetch(
        `${SERVER_ORIGIN}/api/admin/auth/verticals/${verticalId}/delete`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            "auth-token": sessionStorage.getItem("token"),
            Authorization: `Basic ${basicAuth}`,
          },
        }
      );

      const result = await response.json();

      if (response.status >= 400 && response.status < 600) {
        if (response.status === 401) {
          navigate("/admin/login"); // login or role issue
        } else if (response.status === 404) {
          toast.error(result.statusText);
        } else if (response.status === 500) {
          toast.error(result.statusText);
        }
      } else if (response.ok && response.status === 200) {
        refreshScreen();
        // set fields in modal to empty if not refreshing scrn
      } else {
        // for future
      }

      refClose2.current.click();
    } catch (error) {
    }
  }

  const deleteModal = (
    <>
      <button
        ref={ref2}
        type="button"
        className="btn btn-primary d-none"
        data-bs-toggle="modal"
        data-bs-target="#exampleModal3"
      >
        Launch demo modal
      </button>

      <div
        className="modal fade"
        id="exampleModal3"
        tabIndex="-1"
        aria-labelledby="exampleModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5
                className="modal-title text-ff1"
                id="exampleModalLabel"
              >
                Delete vertical
              </h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              />
            </div>
            <div className="modal-body">
              <div style={{ marginBottom: "1rem" }}>
                <label htmlFor="confirm" className="modalLabel">
                  Confirmation
                </label>
                <input
                  type="text"
                  className="modalInput"
                  id="confirm"
                  autoComplete="on"
                  name="confirm"
                  placeholder="Type 'Confirm' to delete"
                  value={confirmText}
                  onChange={onConfirmTextChange}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="modalCloseBtn"
                data-bs-dismiss="modal"
                ref={refClose2}
              >
                Close
              </button>
              <button
                onClick={handleDeleteVertical}
                type="button"
                className="modalDltBtn"
                disabled={confirmText !== "Confirm"}
              >
                Delete Vertical
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  const loader = <Loader />;

  const element = (
    <section id="verticals">
      {allVerticals.length > 0 ? (
        // <CardGrid>
        //   {allVerticals.map((vertical) => (
        //     <div
        //       className="col-lg-4 col-md-6 col-sm-12 cardOuterDiv"
        //       key={vertical._id}
        //     >
        //       <Card
        //         data={vertical}
        //         type="vertical"
        //         onAddViewClick={handleAddOrViewCourses}
        //         onDeleteClick={openDeleteModal}
        //         isReadOnly={isReadOnly}
        //       />
        //       {!isReadOnly && (
        //         <div style={{ marginTop: "8px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        //           {vertical.isHidden && (
        //             <span
        //               style={{
        //                 fontSize: "12px",
        //                 padding: "2px 8px",
        //                 borderRadius: "999px",
        //                 backgroundColor: "#fee2e2",
        //                 color: "#b91c1c",
        //                 fontWeight: 600,
        //               }}
        //             >
        //               Hidden from all users
        //             </span>
        //           )}
        //           <button
        //             type="button"
        //             className="btn btn-sm btn-outline-secondary"
        //             onClick={() =>
        //               handleToggleVisibility(vertical._id, !!vertical.isHidden)
        //             }
        //           >
        //             {vertical.isHidden ? "Unhide Vertical" : "Hide Vertical"}
        //           </button>
        //         </div>
        //       )}
        //     </div>
        //   ))}
        // </CardGrid>
        <CardGrid>
          {allVerticals.map((vertical) => (
            <div className="col-lg-4 col-md-6 col-sm-12 cardOuterDiv" key={vertical._id}>
              <Card
                data={vertical}
                type="vertical"
                onAddViewClick={handleAddOrViewCourses}
                onDeleteClick={openDeleteModal}
                isReadOnly={isReadOnly}

                // ── new props ──
                showVisibilityToggle={!isReadOnly}           // only admins see toggle
                isHidden={vertical.isHidden}
                onToggleVisibility={handleToggleVisibility}
              />
            </div>
          ))}
        </CardGrid>
      ) : (
        <h1 className="nothingText">Sorry, we found nothing</h1>
      )}
    </section>
  );

  return (
    <div className={css.outerDiv}>
      <HeaderCard>
        <BackButton
          text="Back to Dashboard"
          customPath={getDashboardPath()}
        />
        <h1 className="headerTitle">Verticals</h1>
        <hr />
        {isReadOnly ? (
          <div>
            <p className="headerSubtitle">
              You have read-only access to view content verticals.
            </p>
            <div className="alert alert-info" role="alert">
              <strong>Note:</strong> As a Regional Mentor user, you can view
              all content but cannot add, edit, or delete any verticals.
            </div>
          </div>
        ) : (
          <div>
            <p className="headerSubtitle">
              You can View/Add/Delete verticals from here
            </p>
            <p className="headerSubtitle">
              Note: Deleting a vertical is irreversible. Do it at your own
              risk.
            </p>
            <button
              onClick={openAddModal}
              className={`${css.addBtn} commonBtn`}
            >
              Add a new vertical
            </button>
          </div>
        )}
      </HeaderCard>

      {isLoading ? loader : element}

      <button
        ref={ref}
        type="button"
        className="btn btn-primary d-none"
        data-bs-toggle="modal"
        data-bs-target="#exampleModal2"
      >
        Launch demo modal
      </button>

      <div
        className="modal fade"
        id="exampleModal2"
        tabIndex="-1"
        aria-labelledby="exampleModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title text-ff1" id="exampleModalLabel">
                Add a new vertical
              </h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              />
            </div>
            <div className="modal-body">
              <div style={{ marginBottom: "0.8rem" }}>
                <label htmlFor="name" className="modalLabel">
                  Name *
                </label>
                <input
                  type="text"
                  className="modalInput"
                  id="name"
                  name="name"
                  minLength={1}
                  maxLength={validation.verticalModal.name.maxLen}
                  onChange={onAddChange}
                  value={newVertical.name}
                  autoComplete="on"
                />
              </div>
              <div className="mb-3">
                <label htmlFor="description" className="modalLabel">
                  Description *
                </label>
                <input
                  type="text"
                  className="modalInput"
                  id="desc"
                  name="desc"
                  onChange={onAddChange}
                  maxLength={validation.verticalModal.desc.maxLen}
                  value={newVertical.desc}
                  autoComplete="on"
                />
              </div>
              <div className="mb-3">
                <label htmlFor="imgSrc" className="modalLabel">
                  Image Source *
                </label>
                <input
                  type="text"
                  className="modalInput"
                  id="imgSrc"
                  name="imgSrc"
                  onChange={onAddChange}
                  value={newVertical.imgSrc}
                  autoComplete="on"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="modalCloseBtn"
                data-bs-dismiss="modal"
                ref={refClose}
              >
                Close
              </button>
              <button
                onClick={handleAddVertical}
                type="button"
                className="modalAddBtn"
              >
                Add Vertical
              </button>
            </div>
          </div>
        </div>
      </div>

      {deleteModal}
    </div>
  );
};

export default VerticalsPage;
